// TemplatesService — AUTHORITY-SWITCHED. Templates are exposed by TWO server controllers sharing one
// base (BaseTemplateController):
//   - AdminTemplateController  (/admin/template)  — a company's own templates (ADMIN/TRAINER).
//   - SystemTemplateController (/system/template) — the global/system templates (SYSTEM_ADMIN).
// The scope is the caller's own on the server; the ONLY thing the client picks is which route to hit,
// and that follows the caller's role. A super-admin has no company, so it must talk to /system/template
// — never /admin/template. Role comes from the DB-derived authorities via isSuperAdminAuthority.
//
// Migrated to the OpenAPI-generated client: each function dispatches to the admin* OR system* SDK fn
// based on isSuperAdminAuthority(), preserving the exact runtime route switch. Each keeps its old return
// shape (callers read res.data) — the adapter returns `{data}` and re-throws an axios-shaped error.
//
// The server derives the company from the caller's principal (IDOR sweep) — /template has no companyId
// param — so the legacy `companyId` arguments are accepted for signature compatibility but NOT forwarded.
//
// NOT migrated (kept on HttpService + flagged): updateTemplate, updateMetaData, enableTemplate,
// disableTemplate — see the TODO notes on each.

// GROUP-ADMIN LIBRARY: a COMPANY_GROUP_ADMIN owns a private program library served by a THIRD controller,
// GroupAdminTemplateController (/groupAdmin/template) — owner-scoped, assigning only to the group admin's
// authorized companies. Not in the OpenAPI snapshot yet, so (like updateTemplate below) the group-admin
// branch talks to httpService directly.
// TODO(openapi-migration): regenerate the SDK (npm run gen:api:live) once /groupAdmin/* ships to stage,
// then swap these httpService calls for the generated groupAdminTemplateController* functions.
import httpService from "./HttpService";
import {isSuperAdminAuthority, isCompanyGroupAdminAuthority} from "../contexts/AuthContext";
import {
  adminTemplateControllerGetNewTemplate,
  adminTemplateControllerDeleteNewTemplate,
  adminTemplateControllerDuplicateNewTemplate,
  adminTemplateControllerCreateNewTemplate,
  adminTemplateControllerGetAllNew,
  adminTemplateControllerGetStatistics,
  adminTemplateControllerMatchExercises,
  adminTemplateControllerImportTemplate,
  systemTemplateControllerGetNewTemplate,
  systemTemplateControllerDeleteNewTemplate,
  systemTemplateControllerDuplicateNewTemplate,
  systemTemplateControllerCreateNewTemplate,
  systemTemplateControllerGetAllNew,
  systemTemplateControllerGetStatistics,
  systemTemplateControllerMatchExercises,
  systemTemplateControllerImportTemplate,
} from '../api/generated';

// Preserve the legacy axios rejection shape so call sites reading `e.response.data.errorCode` keep working.
const throwLikeAxios = (error, response) => {
  throw {response: {data: error, status: response?.status}};
};

// Base picker for the HttpService functions: a group admin's own library (/groupAdmin/template), else the
// legacy system/admin split (updateTemplate/metaData/enable/disable).
const templateBase = () => {
  if (isCompanyGroupAdminAuthority()) return "groupAdmin/template";
  return isSuperAdminAuthority() ? "system/template" : "admin/template";
};

const GROUP_ADMIN_TEMPLATE_BASE = "groupAdmin/template";

const authToken = () => localStorage.getItem("auth");

// Build a `?a=1&b=2` query string, skipping null/undefined (GET bodies are ignored by the server).
const toQuery = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined) search.append(key, value);
  });
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
};

export const getTemplate = async (templateId) => {
  if (isCompanyGroupAdminAuthority()) {
    const res = await httpService.get(`${GROUP_ADMIN_TEMPLATE_BASE}/${templateId}`, null, authToken());
    return {data: res.data};
  }
  const fn = isSuperAdminAuthority() ? systemTemplateControllerGetNewTemplate : adminTemplateControllerGetNewTemplate;
  const {data, error, response} = await fn({path: {id: templateId}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const deleteTemplate = async (templateId) => {
  if (isCompanyGroupAdminAuthority()) {
    const res = await httpService.delete(`${GROUP_ADMIN_TEMPLATE_BASE}/${templateId}`, null, authToken());
    return {data: res.data};
  }
  const fn = isSuperAdminAuthority() ? systemTemplateControllerDeleteNewTemplate : adminTemplateControllerDeleteNewTemplate;
  const {data, error, response} = await fn({path: {id: templateId}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const duplicateTemplate = async (templateId) => {
  if (isCompanyGroupAdminAuthority()) {
    const res = await httpService.post(`${GROUP_ADMIN_TEMPLATE_BASE}/duplicate/${templateId}`, null, authToken());
    return {data: res.data};
  }
  const fn = isSuperAdminAuthority() ? systemTemplateControllerDuplicateNewTemplate : adminTemplateControllerDuplicateNewTemplate;
  const {data, error, response} = await fn({path: {id: templateId}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const createTemplate = async (metaData) => {
  if (isCompanyGroupAdminAuthority()) {
    const res = await httpService.post(GROUP_ADMIN_TEMPLATE_BASE, metaData, authToken());
    return {data: res.data};
  }
  const fn = isSuperAdminAuthority() ? systemTemplateControllerCreateNewTemplate : adminTemplateControllerCreateNewTemplate;
  const {data, error, response} = await fn({body: metaData});
  if (error) throwLikeAxios(error, response);
  return {data};
};

// TODO(openapi-migration): the server's updateNewTemplate uses @RequestBody String (it manually
// ObjectMapper.readValue's the raw JSON), which springdoc models as `body: string`. Passing the
// template OBJECT (correct wire) fails the typecheck; passing JSON.stringify(template) double-encodes
// the body and breaks the server. Stays on HttpService until the server takes a typed @RequestBody.
export const updateTemplate = async (template) => {
  const token = localStorage.getItem("auth");
  return httpService.put(`${templateBase()}`, template, token);
};

// TODO(openapi-migration): no /template/metaData endpoint in the OpenAPI spec. Stays on HttpService.
export const updateMetaData = async (metaData) => {
  const token = localStorage.getItem("auth");
  return httpService.put(`${templateBase()}/metaData`, metaData, token);
};

// TODO(openapi-migration): no /template/enable/{id} endpoint in the OpenAPI spec. Stays on HttpService.
export const enableTemplate = async (id) => {
  const token = localStorage.getItem("auth");
  return httpService.put(`${templateBase()}/enable/${id}`, null, token);
};

// TODO(openapi-migration): no /template/disable/{id} endpoint in the OpenAPI spec. Stays on HttpService.
export const disableTemplate = async (id) => {
  const token = localStorage.getItem("auth");
  return httpService.put(`${templateBase()}/disable/${id}`, null, token);
};

// The list endpoint uses FLAT pageId/pageSize query params (not a nested Spring Pageable), so it maps
// cleanly to the SDK query. companyId is dropped (server scopes by principal). Same exclusion rules as
// the old URL builder (skip null/undefined/""/"null"/"undefined"/"all").
export const getTemplates = async (
    pageId = 0,
    pageSize = 20,
    numberOfWorkouts,
    experienceLevel,
    keyword,
    bodyPart,
    equipmentType,
    enabled,
    secondaryMuscle,
    secondaryMuscle2,
    gender,
    companyId = null, // eslint-disable-line no-unused-vars
    sortBy = "createdDate",
    sortDirection = "DESC",
    excludeSystem = false
) => {
  // Helper: exclude sentinel/empty values, matching the pre-migration builder.
  const shouldExclude = (value) => {
    const stringValue = String(value).toLowerCase();
    return value === null ||
        value === undefined ||
        value === "" ||
        stringValue === "null" ||
        stringValue === "undefined" ||
        stringValue === "all";
  };

  const query = {pageId, pageSize};

  if (!shouldExclude(numberOfWorkouts) && numberOfWorkouts !== 0) {
    query.numberOfWorkouts = numberOfWorkouts;
  }
  if (!shouldExclude(experienceLevel)) query.experienceLevel = experienceLevel;
  if (!shouldExclude(keyword)) query.keyword = keyword;
  if (!shouldExclude(bodyPart)) query.bodyPart = bodyPart;
  if (!shouldExclude(secondaryMuscle)) query.secondaryMuscle = secondaryMuscle;
  if (!shouldExclude(secondaryMuscle2)) query.secondaryMuscle2 = secondaryMuscle2;
  if (!shouldExclude(equipmentType)) query.equipmentType = equipmentType;
  if (enabled === true) query.enabled = true;
  if (!shouldExclude(gender)) query.gender = gender;
  if (!shouldExclude(sortBy)) query.sortBy = sortBy;
  if (!shouldExclude(sortDirection)) query.sortDirection = sortDirection;
  if (excludeSystem === true) query.excludeSystem = true;

  if (isCompanyGroupAdminAuthority()) {
    const res = await httpService.get(`${GROUP_ADMIN_TEMPLATE_BASE}${toQuery(query)}`, null, authToken());
    return {data: res.data};
  }

  const fn = isSuperAdminAuthority() ? systemTemplateControllerGetAllNew : adminTemplateControllerGetAllNew;
  const {data, error, response} = await fn({query});
  if (error) throwLikeAxios(error, response);
  return {data};
};

// companyId dropped (server scopes by principal; /template/statistics has no query param).
export const getTemplateStats = async (companyId) => { // eslint-disable-line no-unused-vars
  if (isCompanyGroupAdminAuthority()) {
    const res = await httpService.get(`${GROUP_ADMIN_TEMPLATE_BASE}/statistics`, null, authToken());
    return {data: res.data};
  }
  const fn = isSuperAdminAuthority() ? systemTemplateControllerGetStatistics : adminTemplateControllerGetStatistics;
  const {data, error, response} = await fn();
  if (error) throwLikeAxios(error, response);
  return {data};
};

// ==================== CSV Import Methods ====================

/**
 * Match exercise names from CSV to existing exercises using fuzzy matching.
 * Returns match results with candidates for each exercise name.
 */
export const matchExercises = async (exerciseNames) => {
  const fn = isSuperAdminAuthority() ? systemTemplateControllerMatchExercises : adminTemplateControllerMatchExercises;
  const {data, error, response} = await fn({body: {exerciseNames}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

/**
 * Import a template from CSV data with resolved exercises.
 * All exercises must be resolved before import.
 */
export const importTemplate = async (request) => {
  const fn = isSuperAdminAuthority() ? systemTemplateControllerImportTemplate : adminTemplateControllerImportTemplate;
  const {data, error, response} = await fn({body: request});
  if (error) throwLikeAxios(error, response);
  return {data};
};
